import React, { useState, useEffect, useRef } from "react";
import DataTable from "../components/table/DataTable";
import { parseCSV, formatFileSize, Purchase } from "../utils/csvParser";
import { exportToCSV } from "../utils/exportCSV";
import FileUpload from "../components/upload/FileUpload";
import UploadProgress from "../components/upload/UploadProgress";
import pathfinderLogo from "../assets/images/pathfinder-wordmark.svg";
import Checkbox from "../components/table/Checkbox";

type Screen = "upload" | "processing" | "table";

const AuditTool = () => {
    const [currentScreen, setCurrentScreen] = useState<Screen>("upload");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [purchases, setPurchases] = useState<Purchase[]>([]);
    const [headers, setHeaders] = useState<string[]>([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [includeLinks, setIncludeLinks] = useState<boolean>(false);
    const fastTimerRef = useRef<number | null>(null);
    const slowTimerRef = useRef<number | null>(null);
    const auditStartRef = useRef<number | null>(null);

    // Timing helpers
    const formatMs = (ms: number) => `${Math.round(ms)}ms`;

    const handleFileSelect = (file: File) => {
        setSelectedFile(file);
        setCurrentScreen("processing");
        setUploadProgress(5);
        auditStartRef.current = performance.now();
        console.log("⏱️ Audit start:", new Date().toISOString());
        console.time("Audit total");
        
        // Upload CSV to S3 for storage (fire and forget)
        uploadCsvToS3(file);
        
        // Start processing the file
        processFile(file);
    };

    const stopProgressTimers = () => {
        if (fastTimerRef.current !== null) {
            clearInterval(fastTimerRef.current);
            fastTimerRef.current = null;
        }
        if (slowTimerRef.current !== null) {
            clearInterval(slowTimerRef.current);
            slowTimerRef.current = null;
        }
    };

    const startProgressTimers = () => {
        // Fast ramp: 0 -> 50 quickly
        stopProgressTimers();
        setUploadProgress(5);
        fastTimerRef.current = window.setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 50) {
                    if (fastTimerRef.current !== null) {
                        clearInterval(fastTimerRef.current);
                        fastTimerRef.current = null;
                    }
                    return prev;
                }
                return Math.min(prev + 5, 50);
            });
        }, 60);

        // Slow ramp: 50 -> 95, +10% every 5s
        slowTimerRef.current = window.setInterval(() => {
            setUploadProgress((prev) => {
                if (prev < 50) return prev; // wait for fast ramp
                if (prev >= 95) return prev; // hold at 95 until done
                return Math.min(prev + 10, 95);
            });
        }, 2500);
    };

    const processFile = async (file: File) => {
        // Start new progress cycle
        startProgressTimers();
    
        // Read file content
        const reader = new FileReader();
        reader.onload = (e) => {
            console.timeEnd("Read file");
            const content = e.target?.result as string;
            console.log("File loaded, content length:", content.length);
    
            // Parse CSV immediately; no artificial delay
            (async () => {
                console.time("Parse CSV");
                const { purchases: parsedData, headers: parsedHeaders } = parseCSV(content);
                console.timeEnd("Parse CSV");
                console.log("Parsed data in AuditTool:", parsedData.length, "purchases");
                console.log("Headers:", parsedHeaders);
                const linkHeaderName = 'Link';
                let headersAfterLinks = parsedHeaders;
                if (includeLinks) {
                  headersAfterLinks = parsedHeaders.includes(linkHeaderName)
                    ? parsedHeaders
                    : [...parsedHeaders, linkHeaderName];
                }
                const approvalHeaderName = 'Approval Result';
                const headersWithExtras = headersAfterLinks.includes(approvalHeaderName)
                  ? headersAfterLinks
                  : [...headersAfterLinks, approvalHeaderName];
                setPurchases(parsedData);
                setHeaders(headersWithExtras);
    
                // Kick off risk scoring and update when done
                try {
                    // Switch progress to dynamic scoring-based updates
                    stopProgressTimers();
                    console.time("Risk scoring (client) total");
                    const scored = await scorePurchases(parsedData, headersWithExtras, (processed, total) => {
                        const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
                        // Keep below 100 until completion signal and ensure a minimum of 5%
                        setUploadProgress(Math.max(5, Math.min(percent, 99)));
                    }, includeLinks);
                    console.timeEnd("Risk scoring (client) total");
                    setPurchases(scored);
                } catch (e) {
                    console.error('Failed to score purchases', e);
                }
    
                // Finish progress and transition when scoring is done
                stopProgressTimers();
                setUploadProgress(100); // jump to 100% immediately
                console.log("Switching to table screen");
                setCurrentScreen("table");
                console.timeEnd("Audit total");
                if (auditStartRef.current !== null) {
                    const total = performance.now() - auditStartRef.current;
                    console.log("⏱️ Audit end. Total elapsed:", formatMs(total));
                }
            })();
        };
    
        console.time("Read file");
        reader.readAsText(file);
    };

    useEffect(() => {
        // Clean up on unmount
        return () => {
            setUploadProgress(0);
            stopProgressTimers();
        };
    }, []);

    const WEBHOOK_BASE_URL = process.env.REACT_APP_WEBHOOK_BASE_URL;
    const BASE_URL = (WEBHOOK_BASE_URL || '').replace(/\/+$/, '');

    const uploadCsvToS3 = async (file: File) => {
        if (!BASE_URL) {
            return;
        }
        try {
            const presignRes = await fetch(`${BASE_URL}/api/s3/upload-audit-csv`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ filename: file.name, content_type: 'text/csv' }),
            });
            if (!presignRes.ok) {
                return;
            }
            const { uploadUrl } = await presignRes.json();

            const uploadRes = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'text/csv' },
                body: file,
            });
            if (!uploadRes.ok) {
                return;
            }
        } catch (e) {
            // Error uploading CSV; optionally handle silently or report elsewhere
        }
    };

    const getColumnKey = (header: string) =>
      header.trim().replace(/\s+/g, '_').replace(/[()]/g, '');

    function findHeaderKey(headers: string[], candidates: string[]): string | null {
      const normalized = headers.map((h) => ({
        raw: h,
        key: getColumnKey(h),
        lc: h.toLowerCase(),
      }));
      for (const c of candidates) {
        const exact = normalized.find((n) => n.lc === c.toLowerCase());
        if (exact) return exact.key;
      }
      const heur = normalized.find(
        (n) => n.lc.includes('item') && n.lc.includes('desc')
      );
      return heur ? heur.key : null;
    }

    async function scorePurchases(
      purchases: Purchase[],
      headers: string[],
      onProgress?: (processed: number, total: number) => void,
      includeLinks: boolean = false
    ): Promise<Purchase[]> {
      if (!BASE_URL) {
        console.warn('REACT_APP_WEBHOOK_BASE_URL is not set; skipping risk scoring');
        return purchases;
      }

      const descKey = findHeaderKey(headers, ['Item Description', 'Description', 'Item Desc']);
      const vendorKey = findHeaderKey(headers, ['Vendor']);
      const riskKey = getColumnKey('RISK SCORE');
      const linkKey = includeLinks ? getColumnKey('Link') : null;
      const approvalKey = getColumnKey('Approval Result');

      if (!descKey) {
        console.warn('Could not find "Item Description" column. Headers:', headers);
        return purchases;
      }

      console.log('Scoring with base URL:', BASE_URL, 'descKey:', descKey, 'riskKey:', riskKey);

      const updated = [...purchases];
      const maxConcurrent = 1200;

      const totalItems = updated.length;
      const totalBatches = Math.ceil(totalItems / maxConcurrent);
      const scoringStart = performance.now();
      console.log(
        `⚙️ Risk scoring started: items=${totalItems}, batchSize=${maxConcurrent}, batches=${totalBatches}`
      );

      // Progress logger: emit processed row count every 5 seconds
      const counters = { processedCount: 0, rainforestProcessedCount: 0 };
      let progressTickSeconds = 0;
      let progressLogTimer: number | null = null;
      progressLogTimer = window.setInterval(() => {
        progressTickSeconds += 5;
        console.log(`${progressTickSeconds}s - ${counters.processedCount} rows, ${counters.rainforestProcessedCount} rainforest`);
        if (onProgress) onProgress(counters.processedCount, totalItems);
      }, 5000);

      for (let i = 0; i < updated.length; i += maxConcurrent) {
        const batchIndex = Math.floor(i / maxConcurrent) + 1;
        // Enforce 60s spacing between batch starts for large jobs (>1000 items)
        // if (totalItems > 1000) {
        //   const scheduledStart = scoringStart + (batchIndex - 1) * 60000; // 60s slots
        //   const now = performance.now();
        //   if (now < scheduledStart) {
        //     const waitMs = scheduledStart - now;
        //     console.log(`⏳ Waiting ${formatMs(waitMs)} before starting batch ${batchIndex}`);
        //     await new Promise<void>((resolve) => window.setTimeout(resolve, waitMs));
        //   }
        // }
        const batch = updated.slice(i, i + maxConcurrent);
        const batchStart = performance.now();

        let successCount = 0;
        let httpErrorCount = 0;
        let networkErrorCount = 0;
        let timeoutCount = 0;
        const latencies: number[] = [];
        const timedOutForBatch: Array<{ p: Purchase; index: number; item: string; vendor: string | null }> = [];
        const LOG_EACH_REQUEST = false;

        await Promise.all(
          batch.map(async (p, j) => {
            const item = String(p[descKey] ?? '').trim();
            const vendor = vendorKey ? (String(p[vendorKey] ?? '').trim() || null) : null;
            const t0 = performance.now();
            if (linkKey) {
              p[linkKey] = '';
            }
            p[approvalKey] = '';
            if (!item) {
              p[riskKey] = '';
              latencies.push(performance.now() - t0);
              counters.processedCount += 1;
              return;
            }
            const isAmazonVendor = !!vendor && /amazon/i.test(vendor);
            if (includeLinks && isAmazonVendor) {
              try {
                const linkRes = await fetch(`${BASE_URL}/api/rainforest/search`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ search_term: item }),
                });
                if (linkRes.ok) {
                  const linkData = await linkRes.json();
                  if (linkKey) {
                    p[linkKey] = String(linkData?.link || '');
                  }
                }
              } catch (_e) {
                // leave link as empty string
              }
              counters.rainforestProcessedCount += 1;
            }
            let aborted = false;
            const controller = new AbortController();
            const timeoutId = window.setTimeout(() => {
              aborted = true;
              timeoutCount += 1;
              timedOutForBatch.push({ p, index: i + j, item, vendor });
              console.warn('⏳ Risk score request >40s', {
                item: item.slice(0, 120),
                index: i + j,
              });
              controller.abort('timeout');
            }, 30000);
            try {
              const res = await fetch(`${BASE_URL}/api/risk/score`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ item, vendor }),
                signal: controller.signal,
              });
              if (!res.ok) {
                httpErrorCount += 1;
                if (LOG_EACH_REQUEST) {
                  console.warn('Risk score HTTP error', res.status, res.statusText, { item: item.slice(0, 80) });
                }
                p[riskKey] = '';
                latencies.push(performance.now() - t0);
                counters.processedCount += 1;
                clearTimeout(timeoutId);
                return;
              }
              const data = await res.json();
              p[riskKey] = Number(data?.risk_level_25) || '';
              p[approvalKey] = String(data?.approval || '');
              successCount += 1;
              const dt = performance.now() - t0;
              latencies.push(dt);
              if (LOG_EACH_REQUEST) {
                console.log(`✓ Scored in ${formatMs(dt)}:`, item.slice(0, 80));
              }
              counters.processedCount += 1;
              clearTimeout(timeoutId);
            } catch (e) {
              networkErrorCount += 1;
              if (LOG_EACH_REQUEST) {
                console.error('Risk score request failed for item:', item.slice(0, 80), e);
              }
              p[riskKey] = '';
              latencies.push(performance.now() - t0);
              if (!aborted) {
                counters.processedCount += 1;
              }
              clearTimeout(timeoutId);
            }
          })
        );

        // Retry timed-out items immediately once
        if (timedOutForBatch.length > 0) {
          console.log(`⏭️ Retrying timed-out items: count=${timedOutForBatch.length}`);
          await Promise.all(
            timedOutForBatch.map(async ({ p, index, item, vendor }) => {
              const t0r = performance.now();
              const controller2 = new AbortController();
              const timeoutId2 = window.setTimeout(() => {
                console.warn('⏳ Retry >20s (final)', {
                  item: item.slice(0, 120),
                  index,
                });
                controller2.abort('timeout');
              }, 20000);
              try {
                const res2 = await fetch(`${BASE_URL}/api/risk/score`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ item, vendor }),
                  signal: controller2.signal,
                });
                if (!res2.ok) {
                  httpErrorCount += 1;
                  p[riskKey] = '';
                  latencies.push(performance.now() - t0r);
                  counters.processedCount += 1;
                  clearTimeout(timeoutId2);
                  return;
                }
                const data2 = await res2.json();
                p[riskKey] = Number(data2?.risk_level_25) || '';
                p[approvalKey] = String(data2?.approval || '');
                successCount += 1;
                const dt2 = performance.now() - t0r;
                latencies.push(dt2);
                counters.processedCount += 1;
                clearTimeout(timeoutId2);
              } catch (e) {
                networkErrorCount += 1;
                p[riskKey] = '';
                latencies.push(performance.now() - t0r);
                counters.processedCount += 1;
                clearTimeout(timeoutId2);
              }
            })
          );
        }

        // Batch summary metrics
        const batchMs = performance.now() - batchStart;
        latencies.sort((a, b) => a - b);
        const avg = latencies.length ? latencies.reduce((a, b) => a + b, 0) / latencies.length : 0;
        const p50 = latencies.length ? latencies[Math.floor(0.5 * (latencies.length - 1))] : 0;
        const p95 = latencies.length ? latencies[Math.floor(0.95 * (latencies.length - 1))] : 0;
        console.log(
          `📦 Batch ${batchIndex}/${totalBatches} finished in ${formatMs(batchMs)} ` +
          `(size=${batch.length}, ok=${successCount}, http=${httpErrorCount}, net=${networkErrorCount}, timeout=${timeoutCount}, ` +
          `avg=${formatMs(avg)}, p50=${formatMs(p50)}, p95=${formatMs(p95)})`
        );
      }

      const totalMs = performance.now() - scoringStart;
      if (progressLogTimer !== null) {
        clearInterval(progressLogTimer);
        progressLogTimer = null;
      }
      if (onProgress) onProgress(totalItems, totalItems);
      console.log(`Progress final: ${counters.processedCount}/${totalItems} rows`);
      const perItem = totalItems ? totalMs / totalItems : 0;
      console.log(
        `✅ Risk scoring completed in ${formatMs(totalMs)} (≈${formatMs(perItem)} per item)`
      );
      return updated;
    }

    return (
        <div className="min-h-screen bg-cloud-tint">
            {/* Header with PathFinder logo */}
            <header className="p-8 bg-cloud-tint border-b border-border">
                <img 
                    src={pathfinderLogo} 
                    alt="Pathfinder" 
                    className="h-6"
                    style={{ filter: "brightness(0)" }}
                />
            </header>
                <div className="p-16">
                    {currentScreen === "upload" && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    checked={includeLinks}
                                    onChange={setIncludeLinks}
                                    aria-label="Include Links"
                                />
                                <span className="text-charcoal text-base select-none">Include Links</span>
                            </div>
                            <FileUpload onFileSelect={handleFileSelect} />
                        </div>
                    )}

                    {currentScreen === "processing" && selectedFile && (
                        <UploadProgress
                            fileName={selectedFile.name}
                            fileSize={formatFileSize(selectedFile.size)}
                            progress={uploadProgress}
                            status="processing"
                        />
                    )}

                {currentScreen === "table" && (
                    <>
                        {console.log("Rendering table with purchases:", purchases.length)}
                        <DataTable
                            purchases={purchases}
                            headers={headers}
                            onExport={({ purchases: filteredPurchases, groupSize }) => {
                                exportToCSV({
                                    purchases: filteredPurchases,
                                    headers,
                                    groupSize,
                                    filename: `purchases-export-${new Date().toISOString().split('T')[0]}.csv`
                                });
                            }}
                        />
                    </>
                )}
                </div>
        </div>
    );
};

export default AuditTool;

